import { useState, createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { appContext } from './AppContext';
import { isFamilyPlan, familyKidSummary } from './familyPlanUtil';
import { newPlanRow, normalizeKid, resolveKidPlans, kidNeedsPlanResolve, groupLinesToKids } from './planUtils';

export const registrationContex = createContext();

export const initialKid = {
  name: '',
  relationshipType: '',
  relationshipOther: '',
  idType: 'Person_Id',
  personId: '',
  passportId: '',
  birthday: '',
  preBorn: false,
  age: null,
  loanAmount: 0
}
const donorFields = {
  mobile: '',
  phone: '',
  sector: '',
  isMember: '',
  idType: 'Person_Id',
  personId: '',
  passportId: '',
  birthday: '',
  city: '',
  street: '',
  houseNumber: '',
  apartmentNumber: '',
  occupation: '',
  spouseName: '',
  spouseBirthday: '',
  spouseIdType: 'Person_Id',
  spousePersonId: '',
  spousePassportId: '',
  spousePhone: '',
  spouseEmail: '',
  callSource: '',
  otherSource: '',

}


export default function RegistrationContex({ children }) {


  const location = useLocation()

  const { user, plans, data, urlAgent, familyPlans } = useContext(appContext)

  const agent = urlAgent?.agent || urlAgent?.owner || location.state?.urlAgent?.agent || location.state?.urlAgent?.owner || ''

  const [donor, setDonor] = useState({ ...donorFields, agent: agent, callSource: (agent) ? 'Agent' : '', ...user, ...location.state?.user })

  const [kids, setKids] = useState(() => {
    // Family entry (e.g. lead-form "jump to family"): shape the kid like
    // selectFamilySubMode does, so completedKids accepts it once a plan is picked.
    if (location.state?.planMode === 'family') {
      const sub = location.state?.familySubMode || 'preBorn'
      return [{ ...initialKid, id: uuidv4(), preBorn: sub === 'preBorn', age: sub === 'preBorn' ? 0 : null }]
    }
    // Standard kids from the simulator arrive in grouped (multi plan rows) shape - normalize defensively
    if (location.state?.completedKids?.length)
      return location.state.completedKids.map(kid => ({ ...initialKid, ...normalizeKid(kid) }))
    return [{ ...initialKid, plans: [newPlanRow()], id: uuidv4() }]
  });

  const [signed, setSigned] = useState({ joining: false, joiningSignature: null, joiningInvalid: false, interest: false, interestSignature: null, interestInvalid: false })

  // Plan mode: 'standard' (default, current behavior) | 'family'. Family mode lives only on registration.
  const [planMode, setPlanMode] = useState(location.state?.planMode || 'standard')
  // Family sub-mode: null (standard) | 'preBorn' (no children yet, default) | 'hasChildren' (enter oldest child)
  const [familySubMode, setFamilySubMode] = useState(location.state?.familySubMode || (location.state?.planMode === 'family' ? 'preBorn' : null))
  // How many children the donor already has ('hasChildren' only): null | '1' | '2' | '3' | 'more'.
  // Together with the oldest child's age it picks the family plan tier (see familyTierPlans).
  const [familyChildrenCount, setFamilyChildrenCount] = useState(location.state?.familyChildrenCount || null)

  const isFamily = planMode === 'family'

  // Remember the standard-mode kids so switching to family and back doesn't lose them
  const standardKidsRef = useRef(null)

  // Switch between standard / family plan modes.
  // Entering family stashes the standard kids and defaults the sub-mode to 'preBorn' (pre-selected);
  // returning to standard restores the stashed kids (or a fresh kid if none).
  const selectPlanMode = (mode) => {
    if (mode === planMode) return
    if (mode === 'family') {
      standardKidsRef.current = kids
      setPlanMode('family')
      setFamilySubMode('preBorn')
      setFamilyChildrenCount(null)
      setKids([{ ...initialKid, id: uuidv4(), preBorn: true, age: 0 }])
    } else {
      setPlanMode('standard')
      setFamilySubMode(null)
      setFamilyChildrenCount(null)
      setKids((standardKidsRef.current?.length) ? standardKidsRef.current : [{ ...initialKid, plans: [newPlanRow()], id: uuidv4() }])
    }
  }

  // Pick the family sub-mode and reset the single kid (preBorn only for "yet to have children")
  const selectFamilySubMode = (sub) => {
    setFamilySubMode(sub)
    setFamilyChildrenCount(null)
    // preBorn carries age 0 to match the existing preborn convention
    setKids([{ ...initialKid, id: uuidv4(), preBorn: sub === 'preBorn', age: sub === 'preBorn' ? 0 : null }])
  }

  // Children count drives the plan tier - changing it invalidates an already picked plan
  const selectFamilyChildrenCount = (count) => {
    setFamilyChildrenCount(count)
    setKids(prev => prev.map(kid => (kid.plan) ? { ...kid, plan: null } : kid))
  }

  // Update donor on user update
  useEffect(() => {
    let cleanUser = {};

    Object.keys(user).forEach(val => {
      const newVal = user[val];
      cleanUser = newVal ? { ...cleanUser, [val]: newVal } : cleanUser;
    });

    setDonor(prev => ({ ...prev, ...cleanUser }))
  }, [user])

  // Update kids on createdLines or continue Lead kids
  useEffect(() => {
    if (data?.createdLines) {
      if (isFamily)
        // Family lines keyed by kid.id (value may be a list of sub-plan line ids)
        setKids(prev => prev.map(kid => (Object.keys(data.createdLines).includes(kid.id)) ? { ...kid, requestLine: data.createdLines[kid.id] } : kid))
      else
        // Standard createdLines keys are composite `${kid.id}:${plan.route}` - attach requestLine per plan row
        setKids(prev => prev.map(kid => ({
          ...kid,
          plans: kid.plans?.map(row => {
            const key = `${kid.id}:${row.plan?.route}`;
            return (key in data.createdLines) ? { ...row, requestLine: data.createdLines[key] } : row;
          })
        })))
    }

    if (data?.lines && !location.state?.completedKids) {// Continue Lead only when no simulator state Kids
      // Detect family leads (loan-amount driven sub-plan lines) - keep them in single-plan shape
      const hasFamilyLine = data.lines.some(line => {
        const linePlan = (line.plan || line.plan === 0) ? plans?.find(plan => plan.route === line.plan) : null;
        return isFamilyPlan(linePlan) || line.familyPlan || line.loanAmount > 0;
      });
      if (hasFamilyLine)
        setKids(data.lines.map(kid => ({ ...initialKid, id: uuidv4(), ...kid, plan: (kid.plan || kid.plan === 0) ? plans?.find(plan => plan.route === kid.plan) : null })))
      else
        // Standard: group flat SF lines back into kids with plan rows
        setKids(groupLinesToKids(data.lines, plans, initialKid))
    }

    if (data?.signed)
      setSigned(data.signed)


  }, [data, plans, location, isFamily])

  // Re-resolve standard plan objects with fresh DB fields once plans are fetched (covers stale navigation state)
  useEffect(() => {
    if (isFamily || !plans?.length) return
    const needsResolve = kids?.some(kid => !isFamilyPlan(kid.plan) && kidNeedsPlanResolve(kid))
    if (!needsResolve) return
    setKids(prev => prev?.map(kid => isFamilyPlan(kid.plan) ? kid : resolveKidPlans(normalizeKid(kid), plans)))
  }, [plans, kids, isFamily])


  // Flatten standard kids into one entry per (kid, plan row) - keeps single-plan shape for downstream consumers.
  // Family kids are loan-amount driven (single plan) and stay as-is.
  const flattenedKids = kids?.map(kid => kid).sort((a, b) => b.age - a.age)?.flatMap(kid => {
    if (isFamilyPlan(kid.plan)) return [kid];
    if (!((kid.name || kid.preBorn) && kid.age !== undefined)) return [];
    return (kid.plans ?? []).filter(row => row.plan && row.units > 0).map(row => ({
      ...kid,
      id: `${kid.id}:${row.plan.route}`,
      kidId: kid.id,
      plan: row.plan,
      units: row.units,
      ...((row.requestLine) ? { requestLine: row.requestLine } : {}),
      plans: undefined,
    }));
  }) ?? [];

  // Summary kids
  const completedKids = flattenedKids.reduce((acc, kid) => {
    // Family kids are loan-amount driven (no units, different plan shape) - push as-is
    if (isFamilyPlan(kid.plan)) {
      if ((kid.name || kid.preBorn) && kid.age !== undefined && kid?.loanAmount > 0)
        acc.kids.push(kid);
      return acc
    }
    if ((kid.name || kid.preBorn) && kid.age !== undefined && kid.plan && kid?.units > 0) {
      if (kid.plan.totalPay === kid.plan.return) {
        let modifyKid = JSON.parse(JSON.stringify(kid));
        //callculate kid grant  for rest of units (50 /66 %)
        modifyKid.plan.return = (kid.plan.monthsSave >= 192) ? kid.plan.return * 0.66 : kid.plan.return * 0.5;
        //first kid get compleate unit grant
        if (!acc.grantFirstUnit) {
          modifyKid.plan.return = (kid.plan.return + modifyKid.plan.return * (kid.units - 1)) / kid.units;
          modifyKid.grantFirstUnit = true;
        }
        acc.grantFirstUnit = true;
        acc.kids.push(modifyKid);
      }
      else
        acc.kids.push(kid);
    }
    return acc
  }, { kids: [], grantFirstUnit: false }).kids

  const summaryKids = completedKids?.reduce((acc, kid) => {
    if (isFamilyPlan(kid.plan)) {
      const s = familyKidSummary(kid.plan, kid.loanAmount);
      acc.totalUnits += s.units;
      acc.totalMonthlyDonation += s.monthlyDonation;
      acc.totalDonation += s.totalDonation;
      acc.totalMonthlyReturn += s.monthlyReturn;
      acc.totalLoan += s.loan;
      acc.totalReturn += s.grant;
      return acc
    }
    acc.totalUnits += Number(kid?.units);
    acc.totalMonthlyDonation += kid?.units * kid?.plan?.monthlyPay;
    acc.totalDonation += kid?.units * kid?.plan?.totalPay;
    acc.totalMonthlyReturn += kid?.units * kid?.plan?.monthlyReturn;
    acc.totalLoan += kid?.units * kid?.plan?.loan //units * 40000;
    acc.totalReturn += kid?.units * kid?.plan?.return;
    return acc
  }, { totalUnits: 0, totalMonthlyDonation: 0, totalLoan: 0, totalDonation: 0, totalReturn: 0, totalMonthlyReturn: 0 });

  const value = { donor, setDonor, kids, setKids, completedKids, summaryKids, signed, setSigned, agent, planMode, familySubMode, familyChildrenCount, selectPlanMode, selectFamilySubMode, selectFamilyChildrenCount, familyPlans };

  return (
    <registrationContex.Provider value={value} >
      {children}
    </registrationContex.Provider >
  )
}
